import { z } from "zod";
/**
 * How a member works, on four separate axes. A night-shift contact centre
 * agent is desk + shift + restricted; a ward nurse is on_feet + shift. Keeping
 * the axes apart means no member has to pick the least wrong single label.
 *
 * Whether a member can be reached on a device is not one of these: it is read
 * from the channels they have connected, not inferred from the work they do.
 */
export const WORK_CATEGORIES = ["knowledge", "frontline"];
export const WORK_SETTINGS = ["desk", "on_feet"];
export const WORK_SCHEDULES = ["fixed", "shift"];
/** `restricted`: at a screen but not free to step away, as in a contact centre. */
export const WORK_AUTONOMIES = ["free", "restricted"];
/** Who told us. A member or employer value always outranks an inferred one. */
export const WORK_SOURCES = ["member", "employer", "inferred"];
const clock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const workHoursSchema = z.object({
    /** ISO weekdays, 1 = Monday to 7 = Sunday. */
    days: z.array(z.number().int().min(1).max(7)).min(1),
    start: clock.nullable(),
    end: clock.nullable(),
});
/** The work columns as stored. Every field is null until somebody tells us. */
export const storedWorkSchema = z.object({
    category: z.enum(WORK_CATEGORIES).nullable().catch(null),
    setting: z.enum(WORK_SETTINGS).nullable().catch(null),
    schedule: z.enum(WORK_SCHEDULES).nullable().catch(null),
    autonomy: z.enum(WORK_AUTONOMIES).nullable().catch(null),
    hours: workHoursSchema.nullable().catch(null),
    source: z.enum(WORK_SOURCES).nullable().catch(null),
});
/** Our current use case, so it is what a member we know nothing about is treated as. */
export const DEFAULT_WORK = {
    category: "knowledge",
    setting: "desk",
    schedule: "fixed",
    autonomy: "free",
};
/** Monday to Friday, for a fixed schedule with no hours of its own. */
const DEFAULT_WORK_DAYS = [1, 2, 3, 4, 5];
/**
 * Office hours for a fixed schedule with no times of its own. The day starts
 * at half nine, not nine: the first half hour goes on reading the day ahead,
 * and is rarely a meeting.
 */
const DEFAULT_WORK_START = "09:30";
const DEFAULT_WORK_END = "17:00";
export function resolveWorkContext(stored) {
    const parsed = storedWorkSchema.partial().parse(stored ?? {});
    const declared = ["category", "setting", "schedule", "autonomy"].filter((axis) => parsed[axis] != null);
    return {
        category: parsed.category ?? DEFAULT_WORK.category,
        setting: parsed.setting ?? DEFAULT_WORK.setting,
        schedule: parsed.schedule ?? DEFAULT_WORK.schedule,
        autonomy: parsed.autonomy ?? DEFAULT_WORK.autonomy,
        hours: parsed.hours ?? null,
        source: declared.length > 0 || parsed.hours ? (parsed.source ?? "member") : "default",
        declared,
    };
}
/**
 * The work axes a member's onboarding weekday answer declares. "A mix" stores
 * no tag, so it declares nothing and the defaults stand.
 */
export function workFromLifeContext(ids) {
    const setting = ids.includes("on_feet")
        ? "on_feet"
        : ids.includes("desk_based")
            ? "desk"
            : null;
    const schedule = ids.includes("shift_work") ? "shift" : null;
    return { setting, schedule };
}
/**
 * The days this member works, or null when we cannot know them: a shift
 * pattern with no hours given moves week to week, so no day is ruled out.
 */
export function workDays(work) {
    if (work.hours)
        return work.hours.days;
    return work.schedule === "shift" ? null : DEFAULT_WORK_DAYS;
}
/** Whether `at` falls on one of the member's working days, in their timezone. */
export function isWorkDay(work, at, timezone) {
    const days = workDays(work);
    return days === null || days.includes(localClock(at, timezone).weekday);
}
/**
 * Whether the member is at work at `now`. A fixed schedule with no times
 * given works 09:30 to 17:00. A shift with no times could be any hour, so any
 * time on a working day counts. An overnight shift (end before start) belongs
 * to the day it started on, so 02:00 on Saturday is Friday's shift.
 */
export function isWorkingTime(work, now, timezone) {
    const days = workDays(work);
    const { weekday, minutes } = localClock(now, timezone);
    const onDay = (d) => days === null || days.includes(d);
    const fixed = work.schedule === "fixed";
    const start = clockMinutes(work.hours?.start ?? (fixed ? DEFAULT_WORK_START : null));
    const end = clockMinutes(work.hours?.end ?? (fixed ? DEFAULT_WORK_END : null));
    if (start === null || end === null || start === end)
        return onDay(weekday);
    if (start < end)
        return onDay(weekday) && minutes >= start && minutes < end;
    if (minutes >= start)
        return onDay(weekday);
    if (minutes < end)
        return onDay(weekday === 1 ? 7 : weekday - 1);
    return false;
}
/**
 * The declared part of a member's work, as one line for a prompt, or null
 * when nothing was declared. Defaults are never described: a model told
 * "desk worker" will say so to somebody who never told us that.
 */
export function describeWorkContext(work) {
    const bits = [];
    if (work.declared.includes("category")) {
        bits.push(work.category === "frontline" ? "frontline worker" : "knowledge worker");
    }
    if (work.declared.includes("setting")) {
        bits.push(work.setting === "on_feet" ? "on their feet most of the day" : "mostly at a desk");
    }
    if (work.declared.includes("schedule")) {
        bits.push(work.schedule === "shift" ? "works shifts" : "works fixed hours");
    }
    if (work.declared.includes("autonomy") && work.autonomy === "restricted") {
        bits.push("cannot step away freely during work");
    }
    return bits.length > 0 ? bits.join(", ") : null;
}
const WEEKDAYS = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
function localClock(at, timezone) {
    let parts;
    try {
        parts = new Intl.DateTimeFormat("en-GB", {
            timeZone: timezone,
            weekday: "short",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
        }).formatToParts(at);
    }
    catch {
        parts = new Intl.DateTimeFormat("en-GB", {
            timeZone: "UTC",
            weekday: "short",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
        }).formatToParts(at);
    }
    const part = (type) => parts.find((p) => p.type === type)?.value ?? "";
    return {
        weekday: WEEKDAYS[part("weekday")] ?? 1,
        minutes: Number(part("hour")) * 60 + Number(part("minute")),
    };
}
function clockMinutes(value) {
    if (!value)
        return null;
    const [h, m] = value.split(":").map(Number);
    return h === undefined || m === undefined ? null : h * 60 + m;
}
//# sourceMappingURL=work-context.js.map